import { useState, useRef, useEffect, useMemo, type RefObject } from 'react';

import { parseTimeParams } from '../utils/parseTimeParams';

export interface UseVideoTimelineResult {
  duration: number;
  currentTime: number;
  startTime: number | undefined;
  endTime: number | undefined;
  isLoading: boolean;
  showTimeline: boolean;
}

/**
 * Hook to manage video timeline state and timestamp params.
 * Handles video events, initial seek, and timeline visibility.
 */
export function useVideoTimeline(videoRef: RefObject<HTMLVideoElement | null>, src: string): UseVideoTimelineResult {
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const hasSetInitialTime = useRef(false);

  // Parse time parameters from URL
  const { start: startTime, end: endTime } = useMemo(() => parseTimeParams(src), [src]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Reset on effect re-run (handles startTime changes on cached videos)
    // this ensures that the video is re-seeked to the start time when the component re-renders
    hasSetInitialTime.current = false;

    const abortController = new AbortController();
    const { signal } = abortController;

    const handleLoadStart = () => {
      setCurrentTime(0);
      hasSetInitialTime.current = false;
      setIsLoading(true);
    };

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      setIsLoading(false);
    };

    const handleCanPlay = () => {
      setIsLoading(false);
      // Set initial time from URL param (only once)
      if (startTime !== undefined && startTime >= 0 && !hasSetInitialTime.current) {
        hasSetInitialTime.current = true;
        video.currentTime = startTime;
        setCurrentTime(startTime);
      }
    };

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
    };

    video.addEventListener('loadstart', handleLoadStart, { signal });
    video.addEventListener('loadedmetadata', handleLoadedMetadata, { signal });
    video.addEventListener('canplay', handleCanPlay, { signal });
    video.addEventListener('timeupdate', handleTimeUpdate, { signal });

    // Handle already-loaded video (cached/fast load - events already fired)
    if (video.readyState >= 1) handleLoadedMetadata();
    if (video.readyState >= 3) handleCanPlay();

    return () => abortController.abort();
  }, [videoRef, startTime]);

  const showTimeline = duration > 0 && !isLoading && (startTime !== undefined || endTime !== undefined);

  return { duration, currentTime, startTime, endTime, isLoading, showTimeline };
}
